import React from 'react';
import {
  Arrow, AvailabilityTable, CategoryName, ChangeText, Kpi, OpenAndClosed, PaceColumn, RateTable
} from './BriefingParts';
import { DASH, fmt1, fmtDayLong, fmtInt, signed1, toneOf, windowEdgeNote } from './briefingFormat';

const dayRange = (period) => `${fmtDayLong(period.start)} to ${fmtDayLong(period.end)} (${period.days} days)`;

/** Sentence above the KPIs: fewer or more issues, and the critical and high trend. */
const Lead = ({ kpis }) => {
  const rate = kpis.issuesPer30 || {};
  const high = kpis.critHigh || {};
  const fewer = rate.chg <= 0;
  const highWord = high.chg > 0 ? 'but more' : high.chg < 0 ? 'and fewer' : 'and the same number of';
  return (
    <p className="lead">
      {fewer ? 'Fewer' : 'More'} issues in 2026 than in 2025,{' '}
      <span className={fewer ? 'good' : 'bad'}>{fmt1(rate.y25)} per 30 days then {fmt1(rate.y26)}</span>,{' '}
      {highWord} critical and high cases,{' '}
      <span className={toneOf(high.chg) || undefined}>{fmtInt(high.y25)} then {fmtInt(high.y26)}</span>.
    </p>
  );
};

const KpiRow = ({ kpis }) => {
  const pct = (value) => `${value}%`;
  return (
    <div className="kpis">
      <Kpi
        value={<Arrow prev={kpis.issuesPer30.y25} next={kpis.issuesPer30.y26} />}
        label="Issues per 30 days"
        note={`${fmtInt(kpis.issuesReported.y25)} in 2025 · ${fmtInt(kpis.issuesReported.y26)} in 2026`}
      />
      <Kpi
        value={<Arrow prev={kpis.medianCloseDays.y25} next={kpis.medianCloseDays.y26} suffix=" d" />}
        label="Median time to close"
        note={<>Mean <Arrow prev={kpis.meanCloseDays.y25} next={kpis.meanCloseDays.y26} suffix=" days" /></>}
      />
      <Kpi
        value={<Arrow prev={kpis.closedWithin7Pct.y25} next={kpis.closedWithin7Pct.y26} format={pct} betterWhen="higher" />}
        label="Closed within 7 days"
        note={<>Open after 2 weeks: <Arrow prev={kpis.openAfter2wPct.y25} next={kpis.openAfter2wPct.y26} format={pct} /></>}
      />
      <Kpi
        value={<Arrow prev={kpis.critHigh.y25} next={kpis.critHigh.y26} format={fmtInt} />}
        label="Critical or high"
        note={<><Arrow prev={kpis.critHighPer30.y25} next={kpis.critHighPer30.y26} /> per 30 days</>}
      />
    </div>
  );
};

const YearOnYearTable = ({ rows, kpis }) => {
  const hasPer30 = rows.some((row) => /per 30 days/i.test(row.measure || '') && /critical/i.test(row.measure || ''));
  const per30 = kpis.critHighPer30;
  const chg = per30 && per30.y25 != null && per30.y26 != null ? Math.round((per30.y26 - per30.y25) * 10) / 10 : null;
  return (
    <table className="nums">
      <thead>
        <tr><th>Measure</th><th>2025</th><th>2026</th><th>Change</th></tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.measure}>
            <td>{row.measure}</td>
            <td>{row.y25}</td>
            <td>{row.y26}</td>
            <td><ChangeText text={row.chg} value={row.chgNum} betterWhen={row.betterWhen || 'lower'} /></td>
          </tr>
        ))}
        {!hasPer30 && per30 ? (
          <tr>
            <td>Critical or high per 30 days</td>
            <td>{fmt1(per30.y25)}</td>
            <td>{fmt1(per30.y26)}</td>
            <td><ChangeText text={signed1(chg)} value={chg} /></td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
};

const CategoryTable = ({ rows, kpis }) => (
  <RateTable
    rows={rows}
    labelHead="Category"
    firstLabel="2025"
    secondLabel="2026"
    renderName={(row) => <CategoryName name={row.category} />}
    total={(
      <tr>
        <td><strong>All issues</strong></td>
        <td><strong>{fmtInt(kpis.issuesReported.y25)}</strong></td>
        <td><strong>{fmt1(kpis.issuesPer30.y25)}</strong></td>
        <td><strong>{fmtInt(kpis.issuesReported.y26)}</strong></td>
        <td><strong>{fmt1(kpis.issuesPer30.y26)}</strong></td>
        <td><strong><ChangeText text={signed1(kpis.issuesPer30.chg)} value={kpis.issuesPer30.chg} /></strong></td>
      </tr>
    )}
  />
);

export const YearSummary = ({ briefing }) => {
  const { kpis, periods } = briefing;
  return (
    <section>
      <Lead kpis={kpis} />
      <KpiRow kpis={kpis} />
      <OpenAndClosed briefing={briefing} prefix="yr" />
      <h2>Year on year</h2>
      <YearOnYearTable rows={briefing.yearOnYear || []} kpis={kpis} />
      <p className="note">Green is better for the service. Red is worse.</p>
      <p className="note">
        2025 is {dayRange(periods.y25)}. 2026 is {dayRange(periods.y26)}. Rates are per 30 days. The same issue
        reported again the same day, or in the same thread within 7 days, counts once. Connect2, UAT, and internal
        test tickets are not included. Time to close uses closed cases and includes time waiting on {briefing.customer.name}. Use
        the Azure view for the split at {fmtDayLong(periods.azureCutover)}.
      </p>
      <h2>By category</h2>
      <p className="note">Change is the 2026 rate minus the 2025 rate, per 30 days.</p>
      <CategoryTable rows={briefing.categories || []} kpis={kpis} />
    </section>
  );
};

const MonthCells = ({ count, rate, median, note }) => {
  if (count == null) return <><td>{DASH}</td><td>{DASH}</td><td>{DASH}</td></>;
  return (
    <>
      <td>{fmtInt(count)}{note ? <div className="note">{note}</div> : null}</td>
      <td>{fmt1(rate)}</td>
      <td>{median == null ? DASH : `${fmt1(median)} d`}</td>
    </>
  );
};

export const YearDetail = ({ briefing }) => {
  const { coverage, availability } = briefing;
  const raw = coverage.lakeRawInWindow || {};
  const lake = coverage.lake || {};
  const months = (briefing.months && briefing.months.comparison) || [];
  const a25 = (availability && availability.y25) || [];
  const a26 = (availability && availability.y26) || [];
  return (
    <section>
      <nav className="toc">
        <a href="#yr-themes">Themes</a>
        <a href="#yr-months">By month</a>
        <a href="#yr-pace">Resolution pace</a>
        <a href="#yr-outages">Availability</a>
      </nav>
      <h2 id="yr-themes">Themes</h2>
      <p className="note">
        Reporting and other on the summary is scheduled reporting, homepage and charts, security, and other requests
        together.
      </p>
      <RateTable
        rows={briefing.themes || []}
        labelHead="Theme"
        firstLabel="2025"
        secondLabel="2026"
        emptyText="No theme rows from the lake."
      />

      <h2 id="yr-months">By month opened</h2>
      <p className="note">
        {windowEdgeNote(briefing.periods)} A short month is marked, and the rate is still per 30 days.
      </p>
      <table className="nums">
        <thead>
          <tr>
            <th>Month</th><th>2025</th><th>Per 30 days</th><th>Median close</th>
            <th>2026</th><th>Per 30 days</th><th>Median close</th>
          </tr>
        </thead>
        <tbody>
          {months.map((row) => (
            <tr key={row.month}>
              <td>{row.month}</td>
              <MonthCells count={row.y25} rate={row.r25} median={row.med25} note={row.note25} />
              <MonthCells count={row.y26} rate={row.r26} median={row.med26} note={row.note26} />
            </tr>
          ))}
        </tbody>
      </table>
      <p className="note">
        The 2025 extract has {fmtInt(raw.y25)} rows and the 2026 extract {fmtInt(raw.y26)}. After repeat reports and
        internal test tickets are removed, {fmtInt(lake.y25)} issues remain in 2025 and {fmtInt(lake.y26)} in 2026.
      </p>

      <h2 id="yr-pace">Resolution pace</h2>
      <p className="note">Closed issues by how long they stayed open. Dark bar is still open after two weeks.</p>
      <div className="pace">
        <PaceColumn pace={briefing.pace && briefing.pace.y25} title="2025" />
        <PaceColumn pace={briefing.pace && briefing.pace.y26} title="2026" />
      </div>

      <h2 id="yr-outages">Availability</h2>
      <p className="note">
        Cases reporting the service as down, unavailable, or not accessible. 2025 had {a25.length}. 2026 had{' '}
        {a26.length}. Repeat reports are listed against one lead case.
      </p>
      <h3>2025</h3>
      <AvailabilityTable rows={a25} />
      <h3>2026</h3>
      <AvailabilityTable rows={a26} />
    </section>
  );
};

